import { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { DpsSidebarItem } from '@internal/backstage-plugin-dps';
import { ServiceCatalogAuditSidebarItem } from '@internal/backstage-plugin-service-catalog-audit';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ExtensionIcon from '@material-ui/icons/Extension';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import GroupIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarLogo } from './SidebarLogo';
import { OpsicleUserLink } from './OpsicleUserLink';

export const navContent: ExtensionDefinition = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      // Discovery-driven: pull the well-known pages out of the discovered nav
      // items so we can place them (with the app's preferred labels/icons) in
      // the exact legacy order, then auto-include any other discovered pages.
      const catalog = navItems.take('page:catalog');
      const apiDocs = navItems.take('page:api-docs');
      const techdocs = navItems.take('page:techdocs');
      const scaffolder = navItems.take('page:scaffolder');
      // Placed explicitly (as ServiceCatalogAuditSidebarItem) right after
      // DpsSidebarItem below — take it here so it isn't ALSO rendered via
      // the auto-discovered `rest` list further down.
      navItems.take('page:service-catalog-audit/service-catalog-audit');

      // Pages intentionally NOT surfaced as sidebar items (legacy parity):
      navItems.take('page:search'); // reached via the search modal above
      navItems.take('page:catalog-graph'); // reached from entity pages
      navItems.take('page:catalog-import'); // reached from the scaffolder
      navItems.take('page:user-settings'); // rendered in the Settings group below

      // Anything else a discovered plugin contributes shows up here automatically.
      const rest = navItems
        .rest()
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title));

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {/* Global nav, not org-specific */}
            {catalog && (
              <SidebarItem icon={HomeIcon} to={catalog.href} text="Home" />
            )}
            <MyGroupsSidebarItem
              singularTitle="My Squad"
              pluralTitle="My Squads"
              icon={GroupIcon}
            />
            {apiDocs && (
              <SidebarItem icon={ExtensionIcon} to={apiDocs.href} text="APIs" />
            )}
            {techdocs && (
              <SidebarItem icon={LibraryBooks} to={techdocs.href} text="Docs" />
            )}
            {scaffolder && (
              <SidebarItem
                icon={CreateComponentIcon}
                to={scaffolder.href}
                text="Create..."
              />
            )}
            <DpsSidebarItem />
            <ServiceCatalogAuditSidebarItem />
            <OpsicleUserLink />
            {/* End global nav */}
            <SidebarDivider />
            <SidebarScrollWrapper>
              {rest.map(item => (
                <SidebarItem
                  key={item.href}
                  icon={() => item.icon}
                  to={item.href}
                  text={item.title}
                />
              ))}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
